import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Overline, Reveal } from "@/components/Reveal";
import { TESTIMONIALS } from "@/data/site";
import { tr } from "@/i18n";

const ROTATE_MS = 7000;

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = TESTIMONIALS.length;

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, count]);

  const go = useCallback((dir) => setIndex((i) => (i + dir + count) % count), [count]);
  const item = TESTIMONIALS[index];

  return (
    <section data-testid="testimonials-section" className="relative bg-[#211712] py-28 lg:py-40 overflow-hidden grain">
      <div aria-hidden="true" className="pointer-events-none select-none absolute -top-16 left-1/2 -translate-x-1/2 font-serif text-[20rem] leading-none text-gold/[0.04]">&ldquo;</div>
      <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
        <Reveal>
          <Overline>{tr("Client Reflections")}</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6">
            {tr("In their own")} <em className="gold-shimmer italic">{tr("words.")}</em>
          </h2>
        </Reveal>

        <div
          data-testid="testimonials-stage"
          className="relative mt-14 lg:mt-20 min-h-[19rem] sm:min-h-[16rem] lg:min-h-[15rem] flex items-start justify-center"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
            >
              <Quote strokeWidth={1} size={26} className="text-gold mx-auto mb-8 rotate-180" />
              <p data-testid="testimonial-quote" className="font-serif italic font-light text-2xl sm:text-3xl lg:text-[2.35rem] leading-snug text-white/90 max-w-4xl mx-auto">
                {tr(item.quote)}
              </p>
              <footer className="mt-9">
                <p data-testid="testimonial-author" className="text-[0.7rem] uppercase tracking-[0.32em] text-gold">{tr(item.name)}</p>
                <p data-testid="testimonial-role" className="text-[0.62rem] uppercase tracking-[0.24em] text-white/40 mt-2">{tr(item.role)}</p>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex items-center justify-center gap-8">
          <button
            data-testid="testimonial-prev"
            aria-label={tr("Previous testimonial")}
            onClick={() => go(-1)}
            className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <ChevronLeft strokeWidth={1.5} size={18} />
          </button>
          <div className="flex items-center gap-2.5" data-testid="testimonial-dots">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i + 1}`}
                aria-label={tr("Show testimonial")}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-gold" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
              />
            ))}
          </div>
          <button
            data-testid="testimonial-next"
            aria-label={tr("Next testimonial")}
            onClick={() => go(1)}
            className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <ChevronRight strokeWidth={1.5} size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
